import { Icon } from '@iconify/react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import {
  buildMonthWeeks,
  formatOrdinalDay,
  getIsoWeek,
  getMonthWindow,
  isPastIsoWeek,
  isPastMonth,
} from 'modules/shared/utils';
import { useEffect, useMemo, useState } from 'react';

/**
 * EmployeeTaskScheduler
 * @param {Object} props
 * @param {Object} props.selectedEmployee - The selected employee.
 * @param {Function} props.onConfirm - The function to call when the user confirms the selection.
 * @param {Function} props.onClose - The function to call when the user closes the scheduler.
 */
export default function EmployeeTaskScheduler({
  selectedEmployee,
  onConfirm,
  onClose,
  getAvailabilityForWeek,
  availabilityLoading,
}) {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const earliestAllowed = { year: currentYear, month: currentMonth };

  const currentIsoWeek = getIsoWeek(now);

  const [startMonth, setStartMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());
  const [activeMonth, setActiveMonth] = useState(now.getMonth());
  const [selectedIsoWeek, setSelectedIsoWeek] = useState(currentIsoWeek);

  function isBeforeMonth(a, b) {
    return a.year < b.year || (a.year === b.year && a.month < b.month);
  }

  const shiftWindow = (delta) => {
    const candidate = new Date(year, startMonth + delta, 1);

    const candidateMonth = {
      year: candidate.getFullYear(),
      month: candidate.getMonth(),
    };

    if (delta < 0 && isBeforeMonth(candidateMonth, earliestAllowed)) {
      return;
    }

    setYear(candidateMonth.year);
    setStartMonth(candidateMonth.month);

    const newWindow = getMonthWindow(candidateMonth.year, candidateMonth.month);

    const stillVisible = newWindow.some((m) => m.month === activeMonth && m.year === year);

    if (!stillVisible) {
      const snap = newWindow.find((m) => !isPastMonth(m.year, m.month));
      if (snap) setActiveMonth(snap.month);
    }
  };

  const months = useMemo(() => getMonthWindow(year, startMonth), [year, startMonth]);

  const hasPrevWindow = (() => {
    const candidate = new Date(year, startMonth - 1, 1);

    const candidateMonth = {
      year: candidate.getFullYear(),
      month: candidate.getMonth(),
    };

    return !isBeforeMonth(candidateMonth, earliestAllowed);
  })();

  const weeks = useMemo(() => buildMonthWeeks(year, activeMonth), [year, activeMonth]);

  useEffect(() => {
    if (!weeks.length) return;

    const exists = weeks.some(
      (w) => w.isoWeek.year === selectedIsoWeek.year && w.isoWeek.week === selectedIsoWeek.week,
    );

    if (!exists) {
      const next = weeks.find((w) => {
        const availability = getAvailabilityForWeek(w.isoWeek);
        return w.range.end > today && availability?.isAvailable;
      });
      if (next) setSelectedIsoWeek(next.isoWeek);
    }
  }, [weeks, selectedIsoWeek, getAvailabilityForWeek, today]);

  useEffect(() => {
    if (isPastMonth(year, activeMonth)) {
      setActiveMonth(currentMonth);
      setYear(currentYear);
    }
  }, [year, activeMonth, currentMonth, currentYear]);

  const selectedWeekIndex = weeks.findIndex(
    (w) => w.isoWeek.year === selectedIsoWeek.year && w.isoWeek.week === selectedIsoWeek.week,
  );

  const handlePrevWeek = () => {
    for (let i = selectedWeekIndex - 1; i >= 0; i--) {
      const availability = getAvailabilityForWeek(weeks[i].isoWeek);
      if (weeks[i].range.end > today && availability.isAvailable) {
        setSelectedIsoWeek(weeks[i].isoWeek);
        return;
      }
    }
  };

  const handleNextWeek = () => {
    for (let i = selectedWeekIndex + 1; i < weeks.length; i++) {
      const availability = getAvailabilityForWeek(weeks[i].isoWeek);
      if (weeks[i].range.end > today && availability.isAvailable) {
        setSelectedIsoWeek(weeks[i].isoWeek);
        return;
      }
    }
  };

  const hasPrevSelectableWeek = weeks.slice(0, selectedWeekIndex).some((w) => {
    const availability = getAvailabilityForWeek(w.isoWeek);
    return w.range.end > today && availability.isAvailable;
  });

  const hasNextSelectableWeek = weeks.slice(selectedWeekIndex + 1).some((w) => {
    const availability = getAvailabilityForWeek(w.isoWeek);
    return w.range.end > today && availability.isAvailable;
  });

  return (
    <div className='column gap-5 '>
      <Typography variant='h6' fontSize='1rem'>
        {selectedEmployee?.name} Schedule (Select week & timeline)
      </Typography>

      <div className='column'>
        <div className='row row--between'>
          <span className='font-medium'>Select Month</span>
          <span className='font-medium'>Year {year}</span>
        </div>

        <div className='row gap-5 row--between'>
          <IconButton
            type='button'
            onClick={() => shiftWindow(-1)}
            //
            disabled={!hasPrevWindow}
            sx={{
              opacity: !hasPrevWindow ? 0.5 : 1,
              cursor: !hasPrevWindow ? 'default' : 'pointer',
            }}
          >
            <Icon icon='mingcute:left-line' />
          </IconButton>

          <div className='row row--between gap-3'>
            {months.map((m) => {
              const isActive = m.month === activeMonth && m.year === year;
              const isPast = isPastMonth(m.year, m.month);

              return (
                <Button
                  key={`${m.year}-${m.month}`}
                  type='button'
                  onClick={() => {
                    if (isPast) return;
                    setActiveMonth(m.month);
                  }}
                  disabled={isPast}
                  sx={{
                    px: 2,
                    py: 1,
                    width: 120,
                    borderRadius: 4,
                    backgroundColor: (theme) =>
                      isActive ? theme.palette.header.main : alpha(theme.palette.grey[400], 0.4),
                    opacity: isPast ? 0.4 : 1,
                    cursor: isPast ? 'default' : 'pointer',
                  }}
                >
                  {m.label}
                </Button>
              );
            })}
          </div>

          <IconButton type='button' onClick={() => shiftWindow(1)}>
            <Icon icon='mingcute:right-line' />
          </IconButton>
        </div>
      </div>

      <div className='column'>
        <div className='row row--between'>
          <span className='font-medium'>Select Week</span>
        </div>

        <div className='row gap-5 row--between'>
          <IconButton
            type='button'
            onClick={handlePrevWeek}
            disabled={!hasPrevSelectableWeek}
            sx={{
              opacity: !hasPrevSelectableWeek ? 0.5 : 1,
              cursor: !hasPrevSelectableWeek ? 'default' : 'pointer',
            }}
          >
            <Icon icon='mingcute:left-line' />
          </IconButton>

          <div className='row row--between gap-3'>
            {weeks.map((week) => {
              const isSelected =
                week.isoWeek.year === selectedIsoWeek.year &&
                week.isoWeek.week === selectedIsoWeek.week;

              const availability = getAvailabilityForWeek(week.isoWeek);
              const isPast = week.range.end < today;
              const canAssign = availability.isAvailable && !isPast;

              return (
                <div
                  key={`${week.isoWeek.year}-${week.isoWeek.week}`}
                  className='column gap-1 items-center'
                >
                  <Button
                    type='button'
                    onClick={() => setSelectedIsoWeek(week.isoWeek)}
                    disabled={!canAssign}
                    sx={{
                      px: 2,
                      py: 1,
                      width: 120,
                      borderRadius: 4,
                      backgroundColor: (theme) =>
                        isSelected
                          ? theme.palette.header.main
                          : alpha(theme.palette.grey[400], 0.4),
                      opacity: canAssign ? 1 : 0.5,
                      cursor: canAssign ? 'pointer' : 'default',
                    }}
                  >
                    {week.label}
                  </Button>
                  <span>
                    <span>
                      {formatOrdinalDay(week.range.start.getUTCDate())} -{' '}
                      {formatOrdinalDay(week.range.end.getUTCDate())}
                    </span>
                  </span>
                  <span className='text-xs'>
                    {isPast
                      ? 'Unavailable'
                      : availabilityLoading
                        ? 'Loading availability…'
                        : availability.remainingHours > 0
                          ? `${availability.remainingHours} hours left`
                          : 'Unavailable'}
                  </span>
                </div>
              );
            })}
          </div>

          <IconButton
            type='button'
            onClick={handleNextWeek}
            disabled={!hasNextSelectableWeek}
            sx={{
              opacity: !hasNextSelectableWeek ? 0.5 : 1,
              cursor: !hasNextSelectableWeek ? 'default' : 'pointer',
            }}
          >
            <Icon icon='mingcute:right-line' />
          </IconButton>
        </div>
      </div>

      <div className='row gap-5 row--center'>
        <button type='button' className='btn outlined-btn' onClick={onClose}>
          Cancel
        </button>
        <button
          type='button'
          className='btn cta-btn'
          onClick={() => {
            if (!selectedIsoWeek || isPastIsoWeek(selectedIsoWeek)) return;
            onConfirm(selectedIsoWeek);
          }}
          disabled={!selectedIsoWeek || isPastIsoWeek(selectedIsoWeek)}
        >
          Submit
        </button>
      </div>
    </div>
  );
}
